<script setup lang="ts">
import { useField } from 'vee-validate'
import type { DonationFormAmountOptions } from '#shared/types/donations'
import type { OptionsMap } from '~/types/elementsUi'

interface FormPaymentProps {
  name: string
  title?: string
  errorMessage?: string
  fieldInputs?: DonationFormAmountOptions
  overrideDesktop?: boolean
  disabled?: boolean
  hideEuro?: boolean
}

const props = withDefaults(defineProps<FormPaymentProps>(), {
  errorMessage: undefined,
  fieldInputs: () => {
    return {
      low: 10,
      medium: 20,
      high: 50
    }
  }
})

const viewport = useViewport()

const { t, te } = useI18n()

const { value, setValue } = useField<number>(props.name)

type RadioOption = 'first' | 'second' | 'third'
const radioValue = ref<RadioOption>()

const getFieldString = (field: number) => {
  if (props.hideEuro) {
    return field.toString()
  } else if (Number.isInteger(field)) {
    return `€ ${field},-`
  } else {
    return `€ ${field.toFixed(2).replace('.', ',')}`
  }
}

const firstFieldTag = computed(() => getFieldString(props.fieldInputs.low))

const secondFieldTag = computed(() => getFieldString(props.fieldInputs.medium))

const thirdFieldTag = computed(() => getFieldString(props.fieldInputs.high))

const selfAmountInput = ref<number | undefined>(undefined)
watch(selfAmountInput, (newValue) => {
  if (newValue) {
    setValue(newValue)
    firstEurosInput.value = false
    secondEurosInput.value = false
    thirdEurosInput.value = false
  } else {
    resetAmount()
  }
})

const selfInputIsActive = computed(() => selfAmountInput.value !== undefined)

const resetFields = () => {
  firstEurosInput.value = false
  secondEurosInput.value = false
  thirdEurosInput.value = false
  selfAmountInput.value = undefined
}

defineExpose({ resetFields })

const resetAmount = () => {
  if (
    !firstEurosInput.value &&
    !secondEurosInput.value &&
    !thirdEurosInput.value &&
    !selfAmountInput.value
  ) {
    setValue(0)
    radioValue.value = undefined
  }
}

const firstEurosInput = ref<boolean>(false)
watch(firstEurosInput, (newValue) => {
  if (newValue) {
    setValue(props.fieldInputs.low)
    secondEurosInput.value = false
    thirdEurosInput.value = false
    selfAmountInput.value = undefined
    radioValue.value = 'first'
  } else {
    resetAmount()
  }
})
const toggleFirstEurosInput = () => {
  if (!props.disabled) {
    firstEurosInput.value = !firstEurosInput.value
  }
}

const secondEurosInput = ref<boolean>(false)
watch(secondEurosInput, (newValue) => {
  if (newValue) {
    setValue(props.fieldInputs.medium)
    firstEurosInput.value = false
    thirdEurosInput.value = false
    selfAmountInput.value = undefined
    radioValue.value = 'second'
  } else {
    resetAmount()
  }
})
const toggleSecondEurosInput = () => {
  if (!props.disabled) {
    secondEurosInput.value = !secondEurosInput.value
  }
}

const thirdEurosInput = ref<boolean>(false)
watch(thirdEurosInput, (newValue) => {
  if (newValue) {
    setValue(props.fieldInputs.high)
    firstEurosInput.value = false
    secondEurosInput.value = false
    selfAmountInput.value = undefined
    radioValue.value = 'third'
  } else {
    resetAmount()
    selfAmountInput.value = undefined
  }
})
const toggleThirdEurosInput = () => {
  if (!props.disabled) {
    thirdEurosInput.value = !thirdEurosInput.value
  }
}

watch(radioValue, (newValue) => {
  if (newValue === 'first') {
    firstEurosInput.value = true
  } else if (newValue === 'second') {
    secondEurosInput.value = true
  } else if (newValue === 'third') {
    thirdEurosInput.value = true
  }
})
const radioOptions = computed<OptionsMap<RadioOption>[]>(() => {
  return [
    {
      id: 'first',
      name: firstFieldTag.value
    },
    {
      id: 'second',
      name: secondFieldTag.value
    },
    {
      id: 'third',
      name: thirdFieldTag.value
    }
  ]
})

watch(
  value,
  (newValue) => {
    if (selfInputIsActive.value) {
      return
    } else if (newValue === props.fieldInputs.low) {
      firstEurosInput.value = true
      radioValue.value = 'first'
    } else if (newValue === props.fieldInputs.medium) {
      secondEurosInput.value = true
      radioValue.value = 'second'
    } else if (newValue === props.fieldInputs.high) {
      thirdEurosInput.value = true
      radioValue.value = 'third'
    }
  },
  { immediate: true }
)
</script>

<template>
  <fieldset class="field columns mx-0 mobile multiline items-center justify-evenly">
    <legend v-if="title" class="field-label column p-0! full">
      {{ te(title) ? t(title) : title }}
    </legend>

    <div v-if="!!errorMessage" class="column p-0! full">
      <p class="text-danger">
        {{ errorMessage }}
      </p>
    </div>

    <div
      class="column flex! justify-center p-0!"
      :class="
        viewport.isLessThan('desktop') || overrideDesktop ? 'one-third' : 'one-fifth'
      "
    >
      <VRadio
        v-model="radioValue"
        :value="radioOptions[0].id"
        name="payment-option"
        :ui="{ button: 'sr-only', label: 'w-full pl-2 pr-1' }"
      >
        <VTag
          :color="firstEurosInput ? 'success' : 'black'"
          :dark-color="firstEurosInput ? 'success' : 'beige'"
          class="w-full select-none"
          :class="disabled ? 'cursor-not-allowed! opacity-50' : 'cursor-pointer'"
          size="medium"
          @click="toggleFirstEurosInput"
        >
          {{ firstFieldTag }}
        </VTag>
      </VRadio>
    </div>
    <div
      class="column flex! justify-center p-0!"
      :class="
        viewport.isLessThan('desktop') || overrideDesktop ? 'one-third' : 'one-fifth'
      "
    >
      <VRadio
        v-model="radioValue"
        :value="radioOptions[1].id"
        name="payment-option"
        :ui="{ button: 'sr-only', label: 'w-full px-1' }"
      >
        <VTag
          :color="secondEurosInput ? 'success' : 'black'"
          :dark-color="secondEurosInput ? 'success' : 'beige'"
          class="w-full select-none"
          :class="disabled ? 'cursor-not-allowed! opacity-50' : 'cursor-pointer'"
          size="medium"
          @click="toggleSecondEurosInput"
        >
          {{ secondFieldTag }}
        </VTag>
      </VRadio>
    </div>
    <div
      class="column flex! justify-center p-0!"
      :class="
        viewport.isLessThan('desktop') || overrideDesktop ? 'one-third' : 'one-fifth'
      "
    >
      <VRadio
        v-model="radioValue"
        :value="radioOptions[2].id"
        name="payment-option"
        :ui="{ button: 'sr-only', label: 'w-full pl-1 pr-2' }"
      >
        <VTag
          :color="thirdEurosInput ? 'success' : 'black'"
          :dark-color="thirdEurosInput ? 'success' : 'beige'"
          class="w-full select-none"
          :class="disabled ? 'cursor-not-allowed! opacity-50' : 'cursor-pointer'"
          size="medium"
          @click="toggleThirdEurosInput"
        >
          {{ thirdFieldTag }}
        </VTag>
      </VRadio>
    </div>

    <div
      class="column flex! justify-center"
      :class="
        viewport.isLessThan('desktop') || overrideDesktop
          ? 'pt-3! full'
          : 'p-0! two-fifths'
      "
    >
      <VField icon-left="fa-euro-sign">
        <VInput
          v-model="selfAmountInput"
          class="2xl:max-w-[15vw]"
          :disabled="disabled"
          placeholder="placeholders.other"
          type="number"
          step="0.1"
        />
      </VField>
    </div>

    <FormInput aria-hidden="true" hidden label="Hidden amount input" name="amount" />
  </fieldset>
</template>

<style scoped></style>
